import { registerComponent, getComponent } from "video.js";
import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

const textContent = (el, text) => {
  if (typeof el.textContent === "undefined") {
    el.innerText = text;
  } else {
    el.textContent = text;
  }
  return el;
};

const isObject = (value) => !!value && typeof value === "object";

const isTextNode = (value) => isObject(value) && value.nodeType === 3;

const isEl = (value) => isObject(value) && value.nodeType === 1;

const normalizeContent = (content) => {
  // First, invoke content if it is a function. If it produces an array,
  // that needs to happen before normalization.
  if (typeof content === "function") {
    content = content();
  }

  // Next up, normalize to an array, so one or many items can be normalized,
  // filtered, and returned.
  return (Array.isArray(content) ? content : [content])
    .map((value) => {
      // First, invoke value if it is a function to produce a new value,
      // which will be subsequently normalized to a Node of some kind.
      if (typeof value === "function") {
        value = value();
      }

      if (isEl(value) || isTextNode(value)) {
        return value;
      }

      if (typeof value === "string" && /\S/.test(value)) {
        return document.createTextNode(value);
      }

      return undefined;
    })
    .filter((value) => value);
};

const appendContent = (el, content) => {
  normalizeContent(content).forEach((node) => {
    el.appendChild(node);
  });
  return el;
};

const createEl = (
  tagName = "div",
  properties = {},
  attributes = {},
  content,
) => {
  const el = document.createElement(tagName);

  Object.getOwnPropertyNames(properties).forEach((propName) => {
    const val = properties[propName];
    // See #2176
    // We originally were accepting both properties and attributes in the
    // same object, but that doesn't work so well.
    if (
      propName.indexOf("aria-") !== -1 ||
      propName === "role" ||
      propName === "type"
    ) {
      el.setAttribute(propName, val);

      // Handle textContent since it's not supported everywhere and we have a
      // method for it.
    } else if (propName === "textContent") {
      textContent(el, val);
    } else {
      el[propName] = val;
    }
  });

  Object.getOwnPropertyNames(attributes).forEach((attrName) => {
    el.setAttribute(attrName, attributes[attrName]);
  });

  if (content) {
    appendContent(el, content);
  }

  return el;
};

const TTS = getComponent("TextTrackSettings");

const SELECTORS = {
  backgroundColor: {
    selector: ".vjs-bg-color > select",
  },

  backgroundOpacity: {
    selector: ".vjs-bg-opacity > select",
  },

  color: {
    selector: ".vjs-fg-color > select",
  },

  edgeStyle: {
    selector: ".vjs-edge-style > select",
  },

  fontFamily: {
    selector: ".vjs-font-family > select",
  },

  fontPercent: {
    selector: ".vjs-font-percent > select",
  },

  textOpacity: {
    selector: ".vjs-text-opacity > select",
  },

  windowColor: {
    selector: ".window-color > select",
  },

  windowOpacity: {
    selector: ".vjs-window-opacity > select",
  },
};

SELECTORS.windowColor.options = SELECTORS.backgroundColor.options;

function Svg() {
  return (
    <svg
      className="tts__caret-down"
      fill="#fff"
      height="26"
      viewBox="0 0 26 26"
      width="26"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 10l5 5 5-5z" />
      <path d="M0 0h24v24H0z" fill="none" />
    </svg>
  );
}

function Menu({ dialogLabelId }) {
  return (
    <React.Fragment>
      <div className="tts__bg" />
      <div role="document" className="tts">
        <div
          role="heading"
          aria-level="1"
          id={dialogLabelId}
          className="tts__header"
        >
          <div className="tts__heading tts__heading--font  font-size--copy--medium is-active">
            Font Settings
          </div>
          <div className="tts__heading tts__heading--background  font-size--copy--medium">
            Background Settings
          </div>
        </div>
        <div className="tts__selection" />
        <div className="vjs-tracksettings tts__wrapper">
          <div className="vjs-tracksettings tts_container">
            <div data-name="font" className="tts__page hidden">
              <div className="vjs-tracksettings-colors tts__tracksetting">
                <span className="tts__type-title  font-size--copy--medium">
                  Color
                </span>
                <fieldset>
                  <span className="vjs-fg-color tts__select--wrapper">
                    <Svg />
                    <select
                      className="tts__select  font-size--copy--medium"
                      defaultValue="#FFF"
                      id="captions-foreground-color-verse-video-135_component_2439"
                    >
                      <option value="#FFF">White</option>
                      <option value="#000">Black</option>
                      <option value="#F00">Red</option>
                      <option value="#0F0">Green</option>
                      <option value="#00F">Blue</option>
                      <option value="#FF0">Yellow</option>
                      <option value="#F0F">Magenta</option>
                      <option value="#0FF">Cyan</option>
                    </select>
                  </span>
                  <span className="vjs-text-opacity vjs-opacity tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-foreground-opacity-verse-video-135_component_2439"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="1"
                    >
                      <option value="1">Opaque 1</option>
                      <option value="0.5">Semi-Opaque 0.5</option>
                    </select>
                  </span>
                </fieldset>
              </div>
              <div className="vjs-tracksettings-colors tts__tracksetting">
                <span className="tts__type-title  font-size--copy--medium">
                  Font
                </span>
                <fieldset>
                  <span className="vjs-font-family vjs-tracksetting tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-font-family-verse-video-135_component_2439"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="proportionalSansSerif"
                    >
                      <option value="proportionalSansSerif">
                        Proportional Sans-Serif
                      </option>
                      <option value="monospaceSansSerif">
                        Monospace Sans-Serif
                      </option>
                      <option value="proportionalSerif">
                        Proportional Serif
                      </option>
                      <option value="monospaceSerif">Monospace Serif</option>
                      <option value="casual">Casual</option>
                      <option value="script">Script</option>
                      <option value="small-caps">Small Caps</option>
                    </select>
                  </span>
                  <span className="vjs-edge-style vjs-tracksetting tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-edge-style-verse-video-135_component_2439"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="none"
                    >
                      <option value="none">None</option>
                      <option value="raised">Raised</option>
                      <option value="depressed">Depressed</option>
                      <option value="uniform">Uniform</option>
                      <option value="dropshadow">Dropshadow</option>
                    </select>
                  </span>
                </fieldset>
              </div>
              <div className="vjs-tracksettings-colors tts__tracksetting">
                <span className="tts__type-title  font-size--copy--medium">
                  Size
                </span>
                <div className="vjs-font-percent vjs-tracksetting tts__select--wrapper">
                  <Svg />
                  <select
                    id="captions-font-size-verse-video-135_component_2439"
                    className="tts__select  font-size--copy--medium"
                    defaultValue="1.00"
                  >
                    <option value="0.50">50%</option>
                    <option value="0.75">75%</option>
                    <option value="1.00">100%</option>
                    <option value="1.25">125%</option>
                    <option value="1.50">150%</option>
                    <option value="1.75">175%</option>
                    <option value="2.00">200%</option>
                    <option value="3.00">300%</option>
                    <option value="4.00">400%</option>
                  </select>
                </div>
              </div>
            </div>
            <div data-name="background" className="tts__page">
              <div className="vjs-tracksettings-colors tts__tracksetting">
                <span className="tts__type-title  font-size--copy--medium">
                  Background
                </span>
                <fieldset className="vjs-tracksetting">
                  <span className="vjs-bg-color tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-background-color-verse-video-131_component_691"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="#000"
                    >
                      <option value="#000">Black</option>
                      <option value="#FFF">White</option>
                      <option value="#F00">Red</option>
                      <option value="#0F0">Green</option>
                      <option value="#00F">Blue</option>
                      <option value="#FF0">Yellow</option>
                      <option value="#F0F">Magenta</option>
                      <option value="#0FF">Cyan</option>
                      <option value="#72A">Purple</option>
                    </select>
                  </span>
                  <span className="vjs-bg-opacity vjs-opacity tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-background-opacity-verse-video-131_component_691"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="1"
                    >
                      <option value="1">Opaque</option>
                      <option value="0.5">Semi-Transparent</option>
                      <option value="0">Transparent</option>
                    </select>
                  </span>
                </fieldset>
              </div>
              <div className="vjs-tracksettings-colors tts__tracksetting">
                <span className="tts__type-title  font-size--copy--medium">
                  Window
                </span>
                <fieldset className="vjs-tracksetting tts__select--wrapper">
                  <span className="window-color tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-window-color-verse-video-135_component_2439"
                      className="tts__select  font-size--copy--medium"
                      defaultValue="#000"
                    >
                      <option value="#000">Black</option>
                      <option value="#FFF">White</option>
                      <option value="#F00">Red</option>
                      <option value="#0F0">Green</option>
                      <option value="#00F">Blue</option>
                      <option value="#FF0">Yellow</option>
                      <option value="#F0F">Magenta</option>
                      <option value="#0FF">Cyan</option>
                    </select>
                  </span>
                  <span className="vjs-window-opacity vjs-opacity tts__select--wrapper">
                    <Svg />
                    <select
                      id="captions-window-opacity-verse-video-135_component_2439"
                      className="tts__select  font-size--copy--medium"
                      defaultValue=")"
                    >
                      <option value="0">Transparent</option>
                      <option value="0.5">Semi-Transparent</option>
                      <option value="1">Opaque</option>
                    </select>
                  </span>
                </fieldset>
              </div>
            </div>
          </div>
        </div>
        <div className="vjs-tracksettings-controls tts__controls">
          <span className="vjs-default-button font-size--copy--medium">
            Defaults
          </span>
          <span className="vjs-done-button font-size--copy--medium">Done</span>
        </div>
      </div>
    </React.Fragment>
  );
}

Menu.propTypes = {
  dialogLabelId: PropTypes.string.isRequired,
};

class TextTrackSettings extends TTS {
  constructor(player, options) {
    super(player, options);

    this.mainEl = this.$(".tts");

    this.fontButton = this.$(".tts__heading--font");
    this.backgroundButton = this.$(".tts__heading--background");

    this.containerEl = this.$(".tts_container");

    this.fbClicked = this.onFontButtonClicked.bind(this);
    this.bgClicked = this.onBackgroundButtonClicked.bind(this);
    this.bgMove = this.onBackgroundMoved.bind(this);

    this.fontButton.addEventListener("touchend", this.fbClicked);
    this.fontButton.addEventListener("mouseup", this.fbClicked);
    this.backgroundButton.addEventListener("touchend", this.bgClicked);
    this.backgroundButton.addEventListener("mouseup", this.bgClicked);

    this.mainEl.addEventListener("touchmove", this.bgMove);
  }

  onFontButtonClicked() {
    this.fontButton.classList.add("is-active");
    this.backgroundButton.classList.remove("is-active");
    this.containerEl.classList.remove("background--settings");
  }

  onBackgroundButtonClicked() {
    this.fontButton.classList.remove("is-active");
    this.backgroundButton.classList.add("is-active");
    this.containerEl.classList.add("background--settings");
  }

  show() {
    super.show();
    this.onFontButtonClicked();
  }

  onBackgroundMoved(e) {
    e.stopPropagation();
  }

  createEl() {
    const uniqueId = this.id_;
    const dialogLabelId = `TTsettingsDialogLabel-${uniqueId}`;
    const dialogDescriptionId = `TTsettingsDialogDescription-${uniqueId}`;
    const ref = React.createRef();

    ReactDOM.render(
      <div
        aria-describedby={dialogDescriptionId}
        aria-labelledby={dialogLabelId}
        className="vjs-caption-settings vjs-modal-overlay"
        ref={ref}
        role="dialog"
        tabIndex={-1}
      >
        <Menu uniqueId={uniqueId} dialogLabelId={dialogLabelId} />
      </div>,
      document.createElement("div"),
    );

    return ref.current;
  }
}

export const addTextTrackSetting = (key, value, textTrackSettings) => {
  const config = SELECTORS[key];
  const el = textTrackSettings.$(config.selector);
  el.appendChild(
    createEl("option", {
      textContent: textTrackSettings.localize(value[1]),
      value: value[0],
    }),
  );
};

export const registerTextTrackSettings = () => {
  registerComponent("TextTrackSettings", TextTrackSettings);
};

export const videoPlayerLoaded = () => {
  const el = document.querySelector(".tts").parentNode;

  if (el.parentNode.classList.contains("video-js")) {
    const newParent = document.querySelector(".player-container");
    newParent.appendChild(el);
  }
};
